/**
 * RechazoMuestraDialog — Diálogo de rechazo con motivo obligatorio.
 * Si motivo="otro", descripción es obligatoria.
 *
 * Requirements: M4.5.6
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { MotivoRechazo, RechazarMuestraData } from "@/hooks/use-muestras";

const MOTIVOS: { value: MotivoRechazo; label: string }[] = [
  { value: "hemolizada", label: "Hemolizada" },
  { value: "lipemica", label: "Lipémica" },
  { value: "volumen_insuficiente", label: "Volumen insuficiente" },
  { value: "mal_identificada", label: "Mal identificada" },
  { value: "coagulada", label: "Coagulada" },
  { value: "otro", label: "Otro" },
];

interface RechazoMuestraDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: RechazarMuestraData) => void;
  isLoading?: boolean;
}

export function RechazoMuestraDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: RechazoMuestraDialogProps) {
  const { t } = useTranslation();
  const [motivo, setMotivo] = useState<MotivoRechazo | "">("");
  const [descripcion, setDescripcion] = useState("");

  const requiereDescripcion = motivo === "otro";
  const puedeConfirmar = motivo !== "" && (!requiereDescripcion || descripcion.trim().length > 0);

  const handleConfirm = () => {
    if (!motivo || !puedeConfirmar) return;
    onConfirm({ motivo, descripcion: descripcion.trim() });
  };

  const handleOpenChange = (val: boolean) => {
    if (!val) {
      setMotivo("");
      setDescripcion("");
    }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("muestras.rechazo.titulo")}</DialogTitle>
          <DialogDescription>{t("muestras.rechazo.descripcion")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>{t("muestras.rechazo.motivo")}</Label>
            <Select value={motivo} onValueChange={(val) => setMotivo(val as MotivoRechazo)}>
              <SelectTrigger>
                <SelectValue placeholder={t("muestras.rechazo.selecciona_motivo")} />
              </SelectTrigger>
              <SelectContent>
                {MOTIVOS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(motivo === "otro" || descripcion) && (
            <div className="space-y-1">
              <Label htmlFor="descripcion">
                {t("muestras.rechazo.descripcion_label")}
                {requiereDescripcion && <span className="text-destructive ml-1">*</span>}
              </Label>
              <Textarea
                id="descripcion"
                rows={2}
                placeholder={t("muestras.rechazo.descripcion_placeholder")}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!puedeConfirmar || isLoading}
          >
            {isLoading ? t("common.loading") : t("muestras.rechazo.rechazar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
